/**
 * Lightweight client-side monitoring for Supabase realtime connections.
 *
 * Logs (but never throws on) WebSocket-level failures and channel-level
 * status changes so DNS/network issues are visible in the console and
 * available to any error-tracking integration listening on `window`.
 *
 * Safe to import once at app startup. Does NOT modify channel behavior.
 */

import { supabase } from '@/integrations/supabase/client';

type RealtimeIncident = {
  type: 'socket_error' | 'socket_close' | 'channel_status';
  status?: string;
  detail?: unknown;
  url?: string;
  timestamp: string;
};

const MAX_LOGS_PER_MINUTE = 10;
let recentCount = 0;
let windowStart = Date.now();

function shouldLog(): boolean {
  const now = Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    recentCount = 0;
  }
  recentCount += 1;
  return recentCount <= MAX_LOGS_PER_MINUTE;
}

function emit(incident: RealtimeIncident) {
  if (!shouldLog()) return;
  // Console for developer visibility
  // eslint-disable-next-line no-console
  console.warn('[realtime-monitor]', incident);
  // Custom event so other layers (analytics, Sentry shim, etc.) can listen
  try {
    window.dispatchEvent(
      new CustomEvent('realtime:incident', { detail: incident }),
    );
  } catch {
    // Ignore environments without CustomEvent
  }
}

let started = false;

export function startRealtimeMonitor() {
  if (started || typeof window === 'undefined') return;
  started = true;

  try {
    // Access the underlying socket. The realtime client exposes it but the
    // shape isn't part of the public types — keep this defensive.
    const realtime = (supabase as unknown as { realtime?: any }).realtime;
    if (!realtime) return;

    // Hook socket-level events. Supabase's RealtimeClient exposes onError /
    // onClose / onOpen on its underlying Phoenix-style socket.
    if (typeof realtime.onError === 'function') {
      realtime.onError((err: unknown) => {
        emit({
          type: 'socket_error',
          detail: err instanceof Error ? err.message : String(err ?? 'unknown'),
          url: realtime.endPoint,
          timestamp: new Date().toISOString(),
        });
      });
    }

    if (typeof realtime.onClose === 'function') {
      realtime.onClose((evt: { code?: number; reason?: string } | undefined) => {
        // 1000 = normal close. Skip those to avoid noise on logout/navigation.
        if (evt?.code === 1000) return;
        emit({
          type: 'socket_close',
          status: evt?.code != null ? String(evt.code) : undefined,
          detail: evt?.reason || 'abnormal close',
          url: realtime.endPoint,
          timestamp: new Date().toISOString(),
        });
      });
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[realtime-monitor] init failed', err);
  }
}

/**
 * Wrap a `.subscribe(callback)` flow to also report bad statuses
 * (CHANNEL_ERROR, TIMED_OUT, CLOSED) without changing app behavior.
 *
 * Usage:
 *   const channel = supabase.channel('x').on(...).subscribe(
 *     monitorChannelStatus('x')
 *   );
 */
export function monitorChannelStatus(
  channelName: string,
  userCallback?: (status: string, err?: Error) => void,
) {
  return (status: string, err?: Error) => {
    if (
      status === 'CHANNEL_ERROR' ||
      status === 'TIMED_OUT' ||
      status === 'CLOSED'
    ) {
      emit({
        type: 'channel_status',
        status,
        detail: err?.message ?? channelName,
        timestamp: new Date().toISOString(),
      });
    }
    userCallback?.(status, err);
  };
}
